import {
    Button,
    Chip,
    ClickAwayListener,
    Collapse,
    Divider,
    Grow,
    IconButton,
    InputAdornment,
    List,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    TextField,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { Eye, EyeOff, ListFilter, Search, X } from 'lucide-react';
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    ROI_OAR_TYPES,
    ROI_POINT_TYPES,
    ROI_TARGET_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import {
    selectedROIState,
    selectionAutoShownState,
} from '~/state/atoms/structureSet/roiAtoms';
import StructureLayer from './StructureList/Structure';
import StructureDetails from './StructureList/Structure/StructureProperties/StructureDetails/StructureDetails';
import StructureSetDetails from './StructureSetHeader/StructureSetHeader';

function getTypeSortGroup(type: RoiInterpretedType): number {
    if (ROI_TARGET_TYPES.includes(type)) {
        return 0;
    }
    if (ROI_OAR_TYPES.includes(type)) {
        return 1;
    }
    if (ROI_POINT_TYPES.includes(type)) {
        return 3;
    }
    return 2;
}

function getRoiSortGroup(roi: RoiReadModel): number {
    const group = getTypeSortGroup(roi.interpretedType);
    if (group === 2 && roi.isPointRoi) {
        return 3;
    }
    return group;
}

function sortRois(a: RoiReadModel, b: RoiReadModel): number {
    const groupDiff = getRoiSortGroup(a) - getRoiSortGroup(b);
    if (groupDiff !== 0) {
        return groupDiff;
    }
    return a.name.localeCompare(b.name, undefined, { numeric: true });
}

export default function StructureSet({
    structureSet,
    renderExtraDetails,
    readOnly = false,
}: {
    structureSet: StructureSetReadModel;
    renderExtraDetails?: (roi: RoiReadModel) => ReactNode;
    readOnly?: boolean;
}) {
    const intl = useIntl();
    const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const listRef = useRef<HTMLUListElement | null>(null);
    const selectedROINumber = useRecoilValue(selectedROIState);
    const setAutoShown = useSetRecoilState(selectionAutoShownState);
    const { createRoi } = useStructureSet();
    const [createMenuOpen, setCreateMenuOpen] = useState(false);
    const createButtonRef = useRef<HTMLDivElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<Set<RoiInterpretedType>>(
        new Set()
    );
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    const handleCloseCreateMenu = (event: Event) => {
        if (
            createButtonRef.current &&
            createButtonRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setCreateMenuOpen(false);
    };

    const selectedStructure = structureSet?.roiArray?.find(
        (roi) => roi?.roiNumber === selectedROINumber
    );

    const sortedRois = useMemo(() => {
        if (!structureSet?.roiArray) {
            return [];
        }
        return [...structureSet.roiArray].sort(sortRois);
    }, [structureSet]);

    const presentTypes = useMemo(() => {
        const types = new Set<RoiInterpretedType>();
        for (const roi of sortedRois) {
            if (roi.interpretedType) {
                types.add(roi.interpretedType);
            }
        }
        return Array.from(types).sort(
            (a, b) => getTypeSortGroup(a) - getTypeSortGroup(b)
        );
    }, [sortedRois]);

    // Remove stale type filters when structures are deleted
    useEffect(() => {
        setSelectedTypes((prev) => {
            const presentSet = new Set(presentTypes);
            const stale = [...prev].some((t) => !presentSet.has(t));
            if (!stale) {
                return prev;
            }
            const next = new Set([...prev].filter((t) => presentSet.has(t)));
            return next;
        });
    }, [presentTypes]);

    const filteredRois = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return sortedRois.filter((roi) => {
            if (query && !roi.name.toLowerCase().includes(query)) {
                return false;
            }
            if (
                selectedTypes.size > 0 &&
                !selectedTypes.has(roi.interpretedType)
            ) {
                return false;
            }
            return true;
        });
    }, [sortedRois, searchQuery, selectedTypes]);

    const allVisible = useMemo(
        () =>
            filteredRois.length > 0 && filteredRois.every((roi) => roi.visible),
        [filteredRois]
    );

    const handleToggleAllVisibility = useCallback(async () => {
        const newVisibility = !allVisible;
        // Hide-all skips the selected ROI so focus isn't yanked away.
        const targets = newVisibility
            ? filteredRois
            : filteredRois.filter((roi) => roi.roiNumber !== selectedROINumber);
        await Promise.all(
            targets.map((roi) =>
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roi.roiNumber,
                        newVisibility,
                        'user'
                    )
                )
            )
        );
        // Hide-all → selected is now solo-ephemeral; Show-all → no longer.
        setAutoShown(!newVisibility);
    }, [filteredRois, allVisible, selectedROINumber, setAutoShown]);

    const handleToggleType = (type: RoiInterpretedType) => {
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) {
                next.delete(type);
            } else {
                next.add(type);
            }
            return next;
        });
    };

    useEffect(() => {
        const item = itemRefs.current[selectedROINumber];
        const list = listRef.current;
        if (selectedROINumber && item && list) {
            const itemRect = item.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            const isAboveView = itemRect.top < listRect.top;
            const isBelowView = itemRect.bottom > listRect.bottom;
            if (isAboveView || isBelowView) {
                item.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [selectedROINumber]);

    const assignItemRef = (roiNumber: number, el: HTMLDivElement | null) => {
        itemRefs.current[roiNumber] = el;
    };

    return (
        <Box
            position="relative"
            display="grid"
            gridTemplateRows={'auto 1fr auto'}
        >
            <Box>
                <StructureSetDetails structureSet={structureSet} />
                <Box px={1.5}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        ml={-0.25}
                        mt={1}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Search structures..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        sx={{ ml: -0.5 }}
                                    >
                                        <Search size={10} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment
                                        position="end"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <X size={10} />
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: 11,
                                    py: 0.5,
                                },
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => setFilterPanelOpen((prev) => !prev)}
                            aria-label={intl.formatMessage({
                                id: 'toggleTypeFilters',
                            })}
                            sx={{
                                mr: -0.5,
                                color:
                                    filterPanelOpen || selectedTypes.size > 0
                                        ? 'primary.main'
                                        : 'text.secondary',
                            }}
                        >
                            <ListFilter size={12} />
                        </IconButton>
                    </Box>
                    <Collapse in={filterPanelOpen}>
                        {presentTypes.length > 1 && (
                            <Box
                                display="flex"
                                gap={0.5}
                                mt={0.75}
                                mx={-0.25}
                                flexWrap="wrap"
                            >
                                {presentTypes.map((type) => (
                                    <Chip
                                        key={type}
                                        label={type}
                                        size="small"
                                        variant={
                                            selectedTypes.has(type)
                                                ? 'filled'
                                                : 'outlined'
                                        }
                                        onClick={() => handleToggleType(type)}
                                        sx={{
                                            fontSize: 9,
                                            px: 0,
                                            height: 18,
                                            maxWidth: 'none',
                                            borderColor: selectedTypes.has(type)
                                                ? 'primary.main'
                                                : 'divider',
                                            bgcolor: selectedTypes.has(type)
                                                ? 'primary.main'
                                                : 'transparent',
                                            color: selectedTypes.has(type)
                                                ? 'primary.contrastText'
                                                : 'text.secondary',
                                            '&:hover': {
                                                bgcolor: selectedTypes.has(type)
                                                    ? 'primary.dark'
                                                    : 'action.hover',
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Collapse>
                    <Box display="flex" alignItems="center" mt={1}>
                        <IconButton
                            size="small"
                            onClick={handleToggleAllVisibility}
                            aria-label={
                                allVisible
                                    ? 'toggle-all-visibility-eye'
                                    : 'toggle-all-visibility-eyeoff'
                            }
                            data-testid="toggle-all-visibility"
                            sx={{
                                mr: 0.5,
                                ml: -0.75,
                                color: 'primary.main',
                            }}
                        >
                            {allVisible ? (
                                <Eye size={12} />
                            ) : (
                                <EyeOff size={12} />
                            )}
                        </IconButton>
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                fontWeight: 500,
                                flex: 1,
                            }}
                        >
                            {`Structures ${filteredRois.length}`}
                        </Typography>
                        {!readOnly && (
                            <Box position="relative" ref={createButtonRef}>
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() =>
                                        setCreateMenuOpen((prev) => !prev)
                                    }
                                    disabled={structureSet.isApproved}
                                    aria-controls={
                                        createMenuOpen
                                            ? 'create-menu'
                                            : undefined
                                    }
                                    aria-haspopup="menu"
                                    sx={{
                                        fontSize: 11,
                                        py: 0,
                                        px: 0,
                                        minHeight: 0,
                                        minWidth: 0,
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    data-testid="create-roi-button"
                                >
                                    + New
                                </Button>
                                <Popper
                                    sx={{ zIndex: 1 }}
                                    open={createMenuOpen}
                                    anchorEl={createButtonRef.current}
                                    role={undefined}
                                    transition
                                    disablePortal
                                    placement="bottom-end"
                                >
                                    {({ TransitionProps, placement }) => (
                                        <Grow
                                            {...TransitionProps}
                                            style={{
                                                transformOrigin:
                                                    placement === 'bottom-end'
                                                        ? 'right top'
                                                        : 'right bottom',
                                            }}
                                        >
                                            <Paper
                                                elevation={4}
                                                sx={{
                                                    mt: 0.5,
                                                    border: 1,
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <ClickAwayListener
                                                    onClickAway={
                                                        handleCloseCreateMenu
                                                    }
                                                >
                                                    <MenuList
                                                        id="create-menu"
                                                        autoFocusItem
                                                        dense
                                                    >
                                                        <MenuItem
                                                            onClick={() => {
                                                                createRoi();
                                                                setCreateMenuOpen(
                                                                    false
                                                                );
                                                            }}
                                                            disabled={
                                                                structureSet.isApproved
                                                            }
                                                        >
                                                            Structure
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => {
                                                                createRoi(
                                                                    RoiInterpretedType.MARKER
                                                                );
                                                                setCreateMenuOpen(
                                                                    false
                                                                );
                                                            }}
                                                            disabled={
                                                                structureSet.isApproved
                                                            }
                                                        >
                                                            Point
                                                        </MenuItem>
                                                    </MenuList>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box position="relative" height="100%">
                <Box position="absolute" width="100%" height="100%">
                    <List
                        ref={listRef}
                        data-structure-list
                        sx={{
                            position: 'static',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            height: '100%',
                            '& ul': { padding: 0 },
                        }}
                    >
                        {filteredRois.map((roi) => (
                            <div
                                key={`${structureSet.dicomUid}-${roi.roiNumber}`}
                                ref={(el) => assignItemRef(roi.roiNumber, el)}
                            >
                                <StructureLayer roi={roi} />
                            </div>
                        ))}
                    </List>
                </Box>
            </Box>
            {selectedStructure && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                >
                    <Divider />
                    <Box px={1.5} py={1.5}>
                        <StructureDetails roi={selectedStructure} />
                        {renderExtraDetails?.(selectedStructure)}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
